import {
  LoadingSpinner,
  SideDrawer,
  SideDrawerForm,
} from '@sales-center/components'
import Typography from '@mui/material/Typography'
import { ITenantUser } from '@user/store/users/typings'
import * as React from 'react'
import { adminRoleName, guestRoleName } from './constants'
import { ReAssignRecords } from '@user/components/Settings/Users/ReAssignRecords/ReAssignRecords'
import RoleSelect from './RoleSelect'
import FormHelperText from '@mui/material/FormHelperText'
import Box from '@mui/material/Box'
import SectionTitle from './SectionTitle'
import { UpdateMappedUserFormNew } from './UpdateMappedUserFormNew'
import { UpdateUnmappedUserFormNew } from './UpdateUnmappedUserFormNew'
import { SalesTeamAssignmentsNew } from './SalesTeamAssignments/SalesTeamAssignmentsNew'
import { useData } from './hooks/useData'

interface Props {
  onClose: () => void
  open: boolean
  user: ITenantUser | undefined
  variant: 'mapped' | 'unmapped' | undefined
  licenseLeft?: number
  initialActive?: boolean
  setInitialActive?: React.Dispatch<React.SetStateAction<boolean>>
}

export const UpdateUserSideBarNew: React.FC<Props> = (props: Props) => {
  const { user, initialActive, setInitialActive, onClose } = props

  const {
    filteredSalesTeams,
    open,
    title,
    variant,
    reAssignDrawerOpen,
    setReAssignDrawerOpen,
    externalUser,
    role,
    isActive,
    loadingEmployeeList,
    allRoles,
    allSalesTeams,
    handleClose,
    onGuestUserReAssignSuccess,
    onReAssignSuccess,
    handleSubmit,
    handleRoleChange,
    openReAssignDrawerAfterSave,
    disableChangeRole,
    reassignLeads,
    reassignOpps,
  } = useData({
    reopenDrawerOpen: props.open,
    userType: props.variant,
    setInitialActive,
    onClose,
    user,
  })

  return (
    <>
      <SideDrawer
        open={open}
        title={title}
        onClose={handleClose}
        tidalThemeActive={true}
        width={495}
      >
        {loadingEmployeeList === 'pending' && variant === 'mapped' && (
          <LoadingSpinner />
        )}

        {(loadingEmployeeList === 'success' || variant !== 'mapped') && (
          <SideDrawerForm
            tidalMode={true}
            onSave={handleSubmit}
            onCancel={handleClose}
            secondaryBtnLabel="Cancel"
            padding="0 20px"
            primaryBtnLabel="Save"
            containerStyle={{
              py: 0,
              px: 2,
            }}
          >
            {variant === 'mapped' ? (
              <UpdateMappedUserFormNew
                {...props}
                setReAssignDrawerOpen={setReAssignDrawerOpen}
                initialActive={initialActive}
                openReAssignDrawerAfterSave={openReAssignDrawerAfterSave}
              />
            ) : (
              <UpdateUnmappedUserFormNew
                {...props}
                setReAssignDrawerOpen={setReAssignDrawerOpen}
                initialActive={initialActive}
                openReAssignDrawerAfterSave={openReAssignDrawerAfterSave}
              />
            )}
            {props.user && props.user.isSuperAdmin ? (
              <Typography component={'div'} mt={1} variant="body1">
                This is the Super Admin created by the system. You cannot edit
                or change their role
              </Typography>
            ) : variant == 'mapped' && !externalUser ? null : (
              <>
                <SectionTitle
                  title="User Role and Access"
                  linkTitle="Manage Roles"
                  link="/settings/roles"
                />
                {/* ========== S A L E S   C E N T E R   A C C E S S ========== */}

                <>
                  {disableChangeRole && (
                    <Typography component={'div'} mb={1.2} variant="body1">
                      You must re-assign all records assigned to this user
                      before you can change their role.
                    </Typography>
                  )}

                  <Box
                    sx={{
                      backgroundColor:
                        (allRoles && allRoles.length == 0) ||
                        (user && disableChangeRole) ||
                        !isActive
                          ? '#f5f5f5'
                          : '',
                    }}
                  >
                    <RoleSelect
                      label="Role"
                      onChange={handleRoleChange}
                      disabled={
                        allRoles?.length == 0 || disableChangeRole || !isActive
                      }
                    />
                    {role && role.systemCreated ? (
                      <FormHelperText
                        sx={{
                          mx: 1.4,
                          color: (theme) => theme.palette.text.secondary,
                        }}
                      >
                        {role.name === guestRoleName
                          ? 'Guest users only have the ability to add leads and will not be able to override assignment rules. Users with this role will not appear in owner dropdowns and will not deduct from your license count.'
                          : `${role.name} users have top-level access to all Sales Teams and can own records on any team.`}
                      </FormHelperText>
                    ) : null}
                  </Box>
                </>

                {/* ========== S A L E S   T E A M S   &   P O S I T I O N S ========== */}
                {(role && !role.systemCreated) ||
                (!role && !props.user?.isActive) ? (
                  <SalesTeamAssignmentsNew
                    formVariant={variant}
                    salesTeams={
                      props.variant === 'mapped'
                        ? filteredSalesTeams
                        : allSalesTeams
                    }
                    salesTeamPositionIds={props.user?.salesTeamPositionIds}
                    salesTeamResourcesCount={
                      props.user?.roleName === adminRoleName ||
                      props.user?.roleName === guestRoleName
                        ? []
                        : props.user?.salesTeamResourcesCount
                    }
                  />
                ) : null}
              </>
            )}
          </SideDrawerForm>
        )}
      </SideDrawer>

      {reAssignDrawerOpen && (
        <ReAssignRecords
          open={reAssignDrawerOpen}
          onClose={setReAssignDrawerOpen}
          userId={user?.id || ''}
          isGuestReassignment={openReAssignDrawerAfterSave}
          onReAssignSuccess={
            openReAssignDrawerAfterSave
              ? onGuestUserReAssignSuccess
              : onReAssignSuccess
          }
          reassignLeads={reassignLeads}
          reassignOpps={reassignOpps}
        />
      )}
    </>
  )
}
